/* ==========================================================================
   Design tokens
   Dark is the default. Light is a full sibling theme, not an afterthought.
   `[data-theme]` on <html> wins over the media query so the toggle is sticky.
   ========================================================================== */

:root {
  color-scheme: dark;

  --bg: #0b0d10;
  --bg-elev: #12161b;
  --bg-elev-2: #171c22;
  --bg-inset: #0e1215;
  --border: #232a33;
  --border-strong: #35404d;

  --text: #e8ecf1;
  --text-dim: #a7b2c0;
  --text-faint: #78848f;

  --accent: #4fd1c5;
  --accent-strong: #7ee7dd;
  --accent-quiet: rgba(79, 209, 197, 0.12);
  --on-accent: #04211f;

  --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.4);
  --shadow-md: 0 8px 24px -8px rgba(0, 0, 0, 0.6);
  --shadow-lg: 0 24px 60px -20px rgba(0, 0, 0, 0.7);

  --font-body: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
  --font-display: 'Instrument Serif', Georgia, 'Times New Roman', serif;
  --font-mono: 'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace;

  /* Fluid type scale — no media queries needed for headings. */
  --step--1: clamp(0.8rem, 0.77rem + 0.15vw, 0.88rem);
  --step-0: clamp(1rem, 0.96rem + 0.2vw, 1.09rem);
  --step-1: clamp(1.2rem, 1.13rem + 0.35vw, 1.4rem);
  --step-2: clamp(1.5rem, 1.36rem + 0.7vw, 1.95rem);
  --step-3: clamp(1.9rem, 1.6rem + 1.5vw, 2.9rem);
  --step-4: clamp(2.6rem, 1.9rem + 3.4vw, 5rem);

  --container: 68rem;
  --gutter: clamp(1.25rem, 4vw, 3rem);
  --section-y: clamp(4.5rem, 9vw, 8rem);
  --radius: 14px;
  --radius-sm: 9px;
  --radius-pill: 999px;

  --ring: 2px solid var(--accent);
  --ring-offset: 3px;
}

:root[data-theme='light'] {
  color-scheme: light;

  --bg: #fbfaf8;
  --bg-elev: #ffffff;
  --bg-elev-2: #f3f1ec;
  --bg-inset: #f7f5f1;
  --border: #e2ded5;
  --border-strong: #c8c2b5;

  --text: #14181d;
  --text-dim: #4e5a67;
  --text-faint: #74808d;

  --accent: #0d7168;
  --accent-strong: #095c55;
  --accent-quiet: rgba(13, 113, 104, 0.1);
  --on-accent: #ffffff;

  --shadow-sm: 0 1px 2px rgba(20, 24, 29, 0.06);
  --shadow-md: 0 8px 24px -10px rgba(20, 24, 29, 0.18);
  --shadow-lg: 0 24px 60px -24px rgba(20, 24, 29, 0.22);
}

/* Respect the OS when the visitor has not chosen explicitly. */
@media (prefers-color-scheme: light) {
  :root:not([data-theme]) {
    color-scheme: light;

    --bg: #fbfaf8;
    --bg-elev: #ffffff;
    --bg-elev-2: #f3f1ec;
    --bg-inset: #f7f5f1;
    --border: #e2ded5;
    --border-strong: #c8c2b5;

    --text: #14181d;
    --text-dim: #4e5a67;
    --text-faint: #74808d;

    --accent: #0d7168;
    --accent-strong: #095c55;
    --accent-quiet: rgba(13, 113, 104, 0.1);
    --on-accent: #ffffff;

    --shadow-sm: 0 1px 2px rgba(20, 24, 29, 0.06);
    --shadow-md: 0 8px 24px -10px rgba(20, 24, 29, 0.18);
    --shadow-lg: 0 24px 60px -24px rgba(20, 24, 29, 0.22);
  }
}

/* ==========================================================================
   Reset
   ========================================================================== */

*,
*::before,
*::after {
  box-sizing: border-box;
}

* {
  margin: 0;
}

html {
  -webkit-text-size-adjust: 100%;
  scroll-behavior: smooth;
  /* Keeps anchored sections clear of the sticky header. */
  scroll-padding-top: 5.5rem;
}

body {
  background-color: var(--bg);
  color: var(--text);
  font-family: var(--font-body);
  font-size: var(--step-0);
  line-height: 1.65;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  min-height: 100dvh;
  overflow-x: hidden;
}

img,
picture,
svg,
video {
  display: block;
  max-width: 100%;
}

input,
button,
textarea,
select {
  font: inherit;
  color: inherit;
}

button {
  background: none;
  border: 0;
  cursor: pointer;
}

h1,
h2,
h3,
h4 {
  line-height: 1.12;
  text-wrap: balance;
  font-weight: 400;
}

p,
li {
  text-wrap: pretty;
}

ul[class],
ol[class] {
  list-style: none;
  padding: 0;
}

a {
  color: var(--accent);
  text-decoration-thickness: 1px;
  text-underline-offset: 0.22em;
}

a:hover {
  color: var(--accent-strong);
}

/* One consistent, always-visible focus indicator. */
:focus-visible {
  outline: var(--ring);
  outline-offset: var(--ring-offset);
  border-radius: 4px;
}

:focus:not(:focus-visible) {
  outline: none;
}

::selection {
  background: var(--accent);
  color: var(--on-accent);
}

/* ==========================================================================
   Utilities
   ========================================================================== */

.container {
  width: 100%;
  max-width: var(--container);
  margin-inline: auto;
  padding-inline: var(--gutter);
}

/* Visually hidden but still announced by screen readers. */
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

.eyebrow {
  font-family: var(--font-mono);
  font-size: var(--step--1);
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent);
}

/* ==========================================================================
   Motion
   Every animation in the app is gated on this one query.
   ========================================================================== */

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }

  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}

/* ==========================================================================
   Print — the résumé-by-Ctrl-P path
   ========================================================================== */

@media print {
  :root {
    --bg: #fff;
    --bg-elev: #fff;
    --bg-elev-2: #fff;
    --text: #000;
    --text-dim: #333;
    --border: #bbb;
    --accent: #000;
  }

  body {
    font-size: 11pt;
  }

  header[data-site-header],
  [data-no-print] {
    display: none !important;
  }

  a::after {
    content: ' (' attr(href) ')';
    font-size: 9pt;
    color: #555;
  }
}
